// שדה "שאל" (PLAN §3, 09 §4 chat, 11 §1): כל שאלה = ריצה של יצירת ה-chat עם {question, history}. ההיסטוריה = זוגות
// (שאלה, טופס) מ-Screen.turn של התשובות הקודמות (≤ 5) — היצירה בלי מצב בין ריצות. unsupported של בינה-3:
// "לא ניתן לענות מתוך '<פריסה>'" + "פתח בבינה-2" רק ל-creator/admin (member רואה את ההודעה בלי קישור).
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Screen } from '@aibify/display/client';
import { api, ApiError } from '../lib/api.ts';
import { roleAtLeast, useSession } from '../lib/auth.ts';
import { TERMINAL, type RunDetail } from '../lib/contract.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { ErrorView } from './Status.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { IconAlert, IconSparkles } from './brand/icons.tsx';
import { JumpToEnd, useStickToBottom } from './ai/StickToBottom.tsx';

const POLL_MS = 400;
const HISTORY_MAX = 5;

type Turn = { key: number; question: string; runId: string | null; error: unknown };
type ChatTurn = { question: string; form: Record<string, unknown> | null };
type Out = { blocks?: unknown[]; notices?: Array<{ kind: string; textHe: string }>; asOf?: string | null; turn?: ChatTurn };

const outOf = (r: RunDetail | undefined): Out | null => (r?.output && typeof r.output === 'object' ? (r.output as Out) : null);

export function AskPanel({ slug, titleHe, variant = 'inline', suggestions = [] }: { slug: string; titleHe: string; variant?: 'inline' | 'page'; suggestions?: string[] }) {
  const s = useSession();
  const [text, setText] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [sending, setSending] = useState(false);
  const runs = useRef(new Map<number, RunDetail>());
  const seq = useRef(0);
  // הצמדה לסוף רק אחרי ששואלים (initial false): הפאנל יושב בתוך דף היצירה, ופתיחת הדף לא גוללת אליו
  const stick = useStickToBottom({ initial: false });
  const busy = sending || turns.some((t) => t.runId && !t.error && !TERMINAL.has(runs.current.get(t.key)?.status ?? 'queued'));
  const [, force] = useState(0);

  const history = (): ChatTurn[] =>
    turns
      .map((t) => outOf(runs.current.get(t.key))?.turn)
      .filter((x): x is ChatTurn & { form: Record<string, unknown> } => !!x && !!x.form)
      .slice(-HISTORY_MAX);

  const ask = async (q0: string) => {
    const question = q0.trim();
    if (!question || busy) return;
    const key = ++seq.current;
    const h = history();
    setTurns((ts) => [...ts, { key, question, runId: null, error: null }]);
    setText('');
    setSending(true);
    stick.toEnd();
    try {
      const r = await api.createRun(slug, h.length ? { question, history: h } : { question });
      setTurns((ts) => ts.map((t) => (t.key === key ? { ...t, runId: r.runId } : t)));
    } catch (e) {
      setTurns((ts) => ts.map((t) => (t.key === key ? { ...t, error: e } : t)));
    } finally {
      setSending(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void ask(text);
  };

  const canAi2 = roleAtLeast(s?.user.role, 'creator');
  return (
    <section
      ref={stick.contentRef}
      aria-label={titleHe}
      data-testid="ask-panel"
      className={cn('flex flex-col gap-4 rounded-md border border-border bg-card p-4 shadow-card md:p-5', variant === 'page' && 'min-h-[50vh]')}
    >
      <header className="flex items-center gap-3">
        <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-md bg-primary-soft text-primary">
          <IconSparkles size={18} />
        </span>
        <div className="flex min-w-0 flex-col">
          <h2 className="m-0 text-base font-bold">{variant === 'page' ? T.ask.titlePage : T.ask.title}</h2>
          <p className="m-0 text-sm text-muted-foreground">{T.ask.hint}</p>
        </div>
      </header>

      {turns.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-4 p-0" data-testid="ask-turns">
          {turns.map((t) => (
            <li key={t.key} className="flex flex-col gap-2">
              <div className="ms-auto max-w-[85%] rounded-md rounded-se-sm bg-primary-soft px-3 py-2 font-medium text-foreground" data-testid="ask-question">
                <bdi dir="auto">{t.question}</bdi>
              </div>
              <Answer
                turn={t}
                canAi2={canAi2}
                onRun={(r) => {
                  const prev = runs.current.get(t.key);
                  runs.current.set(t.key, r);
                  if (prev?.status !== r.status) force((n) => n + 1);
                }}
              />
            </li>
          ))}
        </ol>
      )}
      {!turns.length && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="ask-suggestions">
          {suggestions.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => void ask(q)}
              className="min-h-11 cursor-pointer rounded-sm border border-border bg-card px-3 text-sm text-foreground transition-colors hover:border-primary/50 hover:bg-muted"
            >
              {q}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="flex items-stretch gap-2" data-testid="ask-form">
        <label htmlFor={`ask-${slug}`} className="sr-only">
          {T.ask.label}
        </label>
        <Input
          id={`ask-${slug}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={T.ask.placeholder}
          maxLength={500}
          autoComplete="off"
          enterKeyHint="send"
          data-testid="ask-input"
        />
        <Button type="submit" disabled={busy || !text.trim()} data-testid="ask-submit" className="shrink-0">
          <IconSparkles size={16} />
          {busy ? T.ask.busy : T.ask.submit}
        </Button>
      </form>
      <div ref={stick.endRef} aria-hidden="true" />
      <JumpToEnd show={busy && !stick.atEnd} onClick={stick.toEnd} className="fixed bottom-[calc(6rem+var(--ab-foot))] z-20 md:bottom-[calc(1.5rem+var(--ab-foot))]" />
    </section>
  );
}

function Answer({ turn, canAi2, onRun }: { turn: Turn; canAi2: boolean; onRun: (r: RunDetail) => void }) {
  const q = useQuery({
    queryKey: ['run', turn.runId],
    queryFn: () => api.getRun(turn.runId!),
    enabled: !!turn.runId,
    refetchInterval: (x) => (x.state.data && TERMINAL.has(x.state.data.status) ? false : POLL_MS),
  });
  useEffect(() => {
    if (q.data) onRun(q.data);
  }, [q.data, onRun]);

  if (turn.error) return <ErrorView error={turn.error} />;
  if (q.isError) return <ErrorView error={q.error} />;
  const r = q.data;
  if (!r || !TERMINAL.has(r.status))
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" data-testid="ask-thinking">
        <span aria-hidden="true" className="relative flex size-2.5">
          <span className="absolute inline-flex size-full animate-ping rounded-sm bg-laser opacity-60" />
          <span className="relative inline-flex size-2.5 rounded-circle bg-laser" />
        </span>
        {T.ask.thinking}
      </div>
    );
  if (r.status !== 'done') return <ErrorView error={new ApiError(0, r.status, r.errorHe ?? T.artifact.status[r.status] ?? T.artifact.failed)} />;
  const out = outOf(r) ?? { blocks: [], notices: [] };
  const unsupported = (out.notices ?? []).filter((n) => n.kind === 'unsupported');
  const rest = { ...out, notices: (out.notices ?? []).filter((n) => n.kind !== 'unsupported') };
  const hasScreen = (rest.blocks?.length ?? 0) > 0 || rest.notices.length > 0;
  return (
    <div className="flex flex-col gap-3" data-testid="ask-answer" data-kind={unsupported.length ? 'unsupported' : hasScreen ? 'answer' : 'empty'}>
      {unsupported.map((n, i) => (
        <div key={i} role="status" data-testid="ask-unsupported" className="flex items-start gap-3 rounded-md border border-degraded/45 bg-degraded/[0.08] p-3">
          <IconAlert size={18} className="mt-0.5 shrink-0 text-degraded" />
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className="m-0">{n.textHe}</p>
            {canAi2 && (
              <Link to={`/ai2?q=${encodeURIComponent(turn.question)}`} className="w-fit font-semibold text-primary underline-offset-4 hover:underline" data-testid="ask-open-ai2">
                {T.ask.openAi2}
              </Link>
            )}
          </div>
        </div>
      ))}
      {hasScreen && <Screen screen={rest} asOf={r.asOf} fetchFile={api.fetchFile} />}
      {!hasScreen && !unsupported.length && <p className="m-0 text-sm text-muted-foreground">{T.ask.empty}</p>}
    </div>
  );
}
